// src/store/appStore.js
// ────────────────────────────────────────────────────────────
// Centralized global state store using Zustand.
// Single source of truth for all realtime application data.
// No TTL caching — Firestore listeners push fresh data directly.
// ────────────────────────────────────────────────────────────
import { create } from 'zustand';

const useAppStore = create((set, get) => ({
  // ── Auth Data ───────────────────────────────────────────
  user: null,
  userProfile: null,

  setUser: (user) => set({ user }),
  setUserProfile: (userProfile) => set({ userProfile }),

  // ── Loading States ──────────────────────────────────────
  // Tracks whether each realtime collection has received its
  // first snapshot. True = still waiting, false = data ready.
  loadingStates: {
    leads: true,
    conversations: true,
    properties: true,
    automations: true,
    formResponses: true,
  },

  setLoading: (key, loading) =>
    set((state) => ({
      loadingStates: { ...state.loadingStates, [key]: loading },
    })),

  // ── Leads ───────────────────────────────────────────────
  leads: null,                     // null = not yet loaded, [] = loaded & empty
  lastContactMap: {},              // { [leadId]: { lastMessageAt, hasMessages } }
  executionMap: null,              // Map<leadId, execution[]> or null
  selectedLeadId: null,
  activeLeadTab: 'all',

  setLeads: (leads) => set({ leads }),
  setLastContactMap: (lastContactMap) => set({ lastContactMap }),
  setExecutionMap: (executionMap) => set({ executionMap }),
  setSelectedLead: (selectedLeadId) => set({ selectedLeadId }),
  setActiveLeadTab: (activeLeadTab) => set({ activeLeadTab }),

  // ── Conversations ───────────────────────────────────────
  conversations: null,
  messagesByConversation: {},
  activeConversationId: null,
  unreadCount: 0,

  /**
   * Replace the full conversations list.
   * Automatically recomputes unread count.
   */
  setConversations: (conversations) => {
    const unreadCount = Array.isArray(conversations)
      ? conversations.filter((c) => (c.unreadCount > 0) || c.unread === true).length
      : 0;
    set({ conversations, unreadCount });
  },

  setMessages: (conversationId, messages) =>
    set((state) => ({
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: messages,
      },
    })),

  setActiveConversation: (conversationId) =>
    set({ activeConversationId: conversationId }),

  setUnreadCount: (unreadCount) => set({ unreadCount }),

  // ── Automations ─────────────────────────────────────────
  automations: null,
  automationRuns: null,
  activeAutomationId: null,
  automationViewMode: 'list',

  setAutomations: (automations) => set({ automations }),
  setAutomationRuns: (automationRuns) => set({ automationRuns }),
  setActiveAutomation: (activeAutomationId) => set({ activeAutomationId }),
  setAutomationViewMode: (automationViewMode) => set({ automationViewMode }),

  // ── Capture Tools ───────────────────────────────────────
  ctProperties: null,
  ctPortfolioPage: null,
  ctFormResponses: null,
  activePropertyId: null,
  activePortfolioPageId: null,

  setCtProperties: (ctProperties) => set({ ctProperties }),
  setCtPortfolioPage: (ctPortfolioPage) => set({ ctPortfolioPage }),
  setCtFormResponses: (ctFormResponses) => set({ ctFormResponses }),
  setActiveProperty: (activePropertyId) => set({ activePropertyId }),
  setActivePortfolioPage: (activePortfolioPageId) => set({ activePortfolioPageId }),

  // ── Dashboard (non-realtime) ────────────────────────────
  // Meetings & Calendly are fetched on-demand, not via realtime
  // listeners. Dashboard stats are derived in the component.
  upcomingMeetings: null,
  calendlyConnected: false,

  setUpcomingMeetings: (upcomingMeetings) => set({ upcomingMeetings }),
  setCalendlyConnected: (calendlyConnected) => set({ calendlyConnected }),

  // ── Settings / User Profile ─────────────────────────────
  settingsProfile: null,
  settingsEmailSignature: null,
  settingsIntegrations: null,

  setSettingsProfile: (settingsProfile) => set({ settingsProfile }),
  setSettingsEmailSignature: (settingsEmailSignature) =>
    set({ settingsEmailSignature }),
  setSettingsIntegrations: (settingsIntegrations) =>
    set({ settingsIntegrations }),

  // ── Global UI State ─────────────────────────────────────
  sidebarCollapsed: false,
  theme: 'light',

  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setTheme: (theme) => set({ theme }),

  // ── Reset (call on logout) ──────────────────────────────
  reset: () =>
    set({
      user: null,
      userProfile: null,
      loadingStates: {
        leads: true,
        conversations: true,
        properties: true,
        automations: true,
        formResponses: true,
      },
      leads: null,
      lastContactMap: {},
      executionMap: null,
      selectedLeadId: null,
      activeLeadTab: 'all',
      conversations: null,
      messagesByConversation: {},
      activeConversationId: null,
      unreadCount: 0,
      automations: null,
      automationRuns: null,
      activeAutomationId: null,
      automationViewMode: 'list',
      ctProperties: null,
      ctPortfolioPage: null,
      ctFormResponses: null,
      activePropertyId: null,
      activePortfolioPageId: null,
      upcomingMeetings: null,
      calendlyConnected: false,
      settingsProfile: null,
      settingsEmailSignature: null,
      settingsIntegrations: null,
      sidebarCollapsed: false,
      theme: 'light',
    }),
}));

export default useAppStore;
